import { Component, Host, Prop, h } from '@stencil/core';
import type { JSX } from '@stencil/core/internal';
import {
  getAssetSrc,
  getTenant,
  renderResponsiveImages,
} from '../../helpers/helpers';

import type {
  Contact,
  ContactLabels,
  ResponsiveImages,
} from '../../helpers/utils/interfaces';
@Component({
  tag: 'cms-contact',
  styleUrl: 'cms-contact.scss',
  shadow: true,
})
export class CmsContact {
  private readonly imageSizes = `(-webkit-min-device-pixel-ratio: 3) 33.33vw,
  (min-resolution: 288dpi) 33.33vw,
  (min-resolution: 3dppx) 33.33vw,
  (-webkit-min-device-pixel-ratio: 2) 50vw,
  (min-resolution: 192dpi) 50vw,
  (min-resolution: 2dppx) 50vw,
  100vw`;

  private readonly contactBackgroundImage =
    getTenant() === 'hornbach' ? 'background_profi.png' : 'contact-bo-bg.jpeg';

  private labels: ContactLabels;

  private contacts: Contact[];
  private group: ResponsiveImages;
  /**
   * set WhatsApp visible or hidden
   */
  @Prop() whatsAppIsVisible: boolean;

  /**
   * Url of the logo
   */
  @Prop() logo: string;

  /**
   * Headline of the intro
   */
  @Prop() introHeadline: string;

  /**
   * Text of the intro
   */
  @Prop() introText: string;

  /**
   * Headline of the contact data section
   */
  @Prop() contactDataHeadline: string;

  /**
   * Mobile-Number of the main contact
   */
  @Prop() contactDataMobile: string;

  /**
   * Phone-Number of the main contact
   */
  @Prop() contactDataPhone: string;

  /**
   * Fax-Number of the main contact
   */
  @Prop() contactDataFax: string;

  /**
   * Mail of the main contact
   */
  @Prop() contactDataMail: string;

  /**
   * List of contacts
   */
  @Prop() contactList: Contact[] | string = [];

  /**
   * Group-Picture
   */
  @Prop() groupPicture: ResponsiveImages | string;

  /**
   * Text that describes the Group-Picture @see renderGroupPicture
   */
  @Prop() groupPictureText: string;

  /**
   * Text of the quote
   */
  @Prop() quote: string;

  /**
   * Link of the CTA
   */
  @Prop() ctaLink: string;

  /**
   * Text of the CTA
   */
  @Prop() ctaText: string;

  /**
   * Static labels required for this component
   */
  @Prop() dictionary: string | ContactLabels;

  private readonly contactLabelPlaceholder = '%VALUE%';

  componentWillLoad() {
    // Global texts
    this.labels =
      typeof this.dictionary === 'string'
        ? JSON.parse(this.dictionary)
        : this.dictionary;
    // List of contacts
    if (this.contactList) {
      this.contacts =
        typeof this.contactList === 'string'
          ? JSON.parse(this.contactList)
          : this.contactList;
    }
    // Group-Picture
    if (this.groupPicture) {
      this.group =
        typeof this.groupPicture === 'string'
          ? JSON.parse(this.groupPicture)
          : this.groupPicture;
    }
  }

  private cleanPhoneNumber(phoneNumber: string): string {
    /*
      Remove (0), all special characters and leading zero.
      Not possible in one regex bc. removing special characters would remove the brackets from '(0)'
      and leading zeroes are not detectable before '+' is removed
    */
    if (phoneNumber) {
      return phoneNumber
        .replace(/\(0\)/gm, '')
        .replace(/\+?\D*/gm, '')
        .replace(/^0*/m, '');
    }
    return '';
  }

  private renderLogo(): JSX.Element {
    return (
      <div class='logo-container'>
        <a title='hornbach' href={this.ctaLink} target='_self'>
          <img width='85' height='80' alt='' class='logo' src={this.logo} />
        </a>
      </div>
    );
  }

  private renderIntroText(): JSX.Element {
    return (
      <div class='intro-container'>
        {this.introHeadline ? (
          <div class='intro-headline-wrapper'>
            <cms-headline
              style={{ '--cms-spacing-top': '0' }}
              headlineText={this.introHeadline}
              headlineTag='h2'
              headlinePosition='center'
              isAdaptive={true}
            />
          </div>
        ) : (
          ''
        )}
        {this.introText ? (
          <div class='intro-text' innerHTML={this.introText} />
        ) : (
          ''
        )}
      </div>
    );
  }

  private parseLabel(label: string, value: string): string {
    return this.labels[label].replace(this.contactLabelPlaceholder, value);
  }

  private renderMobileData(): JSX.Element {
    return this.contactDataMobile ? (
      <div class='contact-data-cell'>
        <cms-icon
          size={'md'}
          icon={'smartphone'}
          colorToken={
            '--cms-semanticColors-base-inverted-medium-iconOnBackground'
          }
          alt=''
        />
        <cms-link
          href={`tel:${this.contactDataMobile}`}
          type='standalone'
          linkTitle={this.parseLabel(
            'accessibleNameMobile',
            this.contactDataMobile,
          )}
          content={this.contactDataMobile}
          size='md'
          spacing='none'
        />
      </div>
    ) : (
      ''
    );
  }

  private renderPhoneData(): JSX.Element {
    return this.contactDataPhone ? (
      <div class='contact-data-cell'>
        <cms-icon
          size={'md'}
          icon={'phone'}
          colorToken={
            '--cms-semanticColors-base-inverted-medium-iconOnBackground'
          }
          alt=''
        />
        <cms-link
          href={`tel:${this.contactDataPhone}`}
          type='standalone'
          linkTitle={this.parseLabel(
            'accessibleNamePhone',
            this.contactDataPhone,
          )}
          content={this.contactDataPhone}
          size='md'
          spacing='none'
        />
      </div>
    ) : (
      ''
    );
  }

  private renderFaxData(): JSX.Element {
    return this.contactDataFax ? (
      <div class='contact-data-cell'>
        <cms-icon
          size={'md'}
          icon={'fax'}
          colorToken={
            '--cms-semanticColors-base-inverted-medium-iconOnBackground'
          }
          alt=''
        />
        <cms-link
          href={`fax:${this.contactDataFax}`}
          type='standalone'
          linkTitle={this.parseLabel('accessibleNameFax', this.contactDataFax)}
          content={this.contactDataFax}
          size='md'
          spacing='none'
        />
      </div>
    ) : (
      ''
    );
  }

  private renderWhatsapp(): JSX.Element {
    return this.contactDataMobile && this.whatsAppIsVisible ? (
      <div class='contact-data-cell'>
        <cms-icon
          size={'md'}
          icon={'socialWhatsapp'}
          colorToken={
            '--cms-semanticColors-base-inverted-medium-iconOnBackground'
          }
          alt=''
        />
        <cms-link
          href={`https://wa.me/${this.cleanPhoneNumber(
            this.contactDataMobile,
          )}`}
          type='standalone'
          linkTitle={this.parseLabel(
            'accessibleNameWhatsApp',
            this.contactDataMobile,
          )}
          content={this.labels.whatsapp}
          size='md'
          spacing='none'
        />
      </div>
    ) : (
      ''
    );
  }

  private renderMail(): JSX.Element {
    return this.contactDataMail ? (
      <div class='contact-data-cell'>
        <cms-icon
          size={'md'}
          icon={'mail'}
          colorToken={
            '--cms-semanticColors-base-inverted-medium-iconOnBackground'
          }
          alt=''
        />
        <cms-link
          href={`mailto:${this.contactDataMail}`}
          type='standalone'
          linkTitle={this.parseLabel(
            'accessibleNameMail',
            this.contactDataMail,
          )}
          content={this.contactDataMail}
          size='md'
          spacing='none'
        />
      </div>
    ) : (
      ''
    );
  }

  private renderContactData(): JSX.Element {
    return (
      <div class='contact-data-container'>
        <div class='contact-data-headline-wrapper'>
          <span class='contact-data-headline'>{this.contactDataHeadline}</span>
        </div>
        <div class='contact-data-table'>
          {this.contactDataMobile ||
          this.contactDataPhone ||
          this.contactDataMail ? (
            <div class='row'>
              {[
                this.renderMobileData(),
                this.renderPhoneData(),
                this.renderFaxData(),
              ]}
            </div>
          ) : (
            ''
          )}
          {(this.contactDataMobile && this.whatsAppIsVisible) ||
          this.contactDataMail ? (
            <div class='row'>{[this.renderWhatsapp(), this.renderMail()]}</div>
          ) : (
            ''
          )}
        </div>
      </div>
    );
  }

  private renderContact(contact: Contact, index: number): JSX.Element {
    const length = this.contacts.length;
    const indexClass =
      length === 1 ? 'once' : length === 2 && index === 0 ? 'twice' : '';

    return (
      <div class={`contact-container ${indexClass}`}>
        <figure class='contact-image-container'>
          {renderResponsiveImages(
            contact.picture as ResponsiveImages,
            this.imageSizes,
            contact.description,
            '',
          )}
          <figcaption>{contact.description}</figcaption>
        </figure>
        <div class='contact-info-container'>
          <ul class='contact-list'>
            {contact.phone ? (
              <li>
                <cms-icon
                  size={'sm'}
                  icon={'phone'}
                  colorToken={
                    '--cms-semanticColors-base-inverted-medium-iconOnBackground'
                  }
                  alt=''
                />
                <cms-link
                  href={`tel:${contact.phone}`}
                  type='standalone'
                  linkTitle={this.parseLabel(
                    'accessibleNamePhone',
                    contact.phone,
                  )}
                  content={contact.phone}
                  size='md'
                  spacing='none'
                />
              </li>
            ) : (
              ''
            )}
            {contact.mobile ? (
              <li>
                <cms-icon
                  size={'sm'}
                  icon={'smartphone'}
                  colorToken={
                    '--cms-semanticColors-base-inverted-medium-iconOnBackground'
                  }
                  alt=''
                />
                <cms-link
                  href={`tel:${contact.mobile}`}
                  type='standalone'
                  linkTitle={this.parseLabel(
                    'accessibleNameMobile',
                    contact.mobile,
                  )}
                  content={contact.mobile}
                  size='md'
                  spacing='none'
                />
              </li>
            ) : (
              ''
            )}
            {contact.mail ? (
              <li>
                <cms-icon
                  size={'sm'}
                  icon={'mail'}
                  colorToken={
                    '--cms-semanticColors-base-inverted-medium-iconOnBackground'
                  }
                  alt=''
                />
                <cms-link
                  href={`mailto:${contact.mail}`}
                  type='standalone'
                  linkTitle={this.parseLabel(
                    'accessibleNameMail',
                    contact.mail,
                  )}
                  content={contact.mail}
                  size='md'
                  spacing='none'
                />
              </li>
            ) : (
              ''
            )}
            {contact.mobile && this.whatsAppIsVisible ? (
              <li>
                <cms-icon
                  size={'sm'}
                  icon={'socialWhatsapp'}
                  colorToken={
                    '--cms-semanticColors-base-inverted-medium-iconOnBackground'
                  }
                  alt=''
                />
                <cms-link
                  href={`https://wa.me/${this.cleanPhoneNumber(
                    contact.mobile,
                  )}`}
                  type='standalone'
                  linkTitle={this.parseLabel(
                    'accessibleNameWhatsApp',
                    contact.mobile,
                  )}
                  content={this.labels.whatsapp}
                  size='md'
                  spacing='none'
                />
              </li>
            ) : (
              ''
            )}
          </ul>
        </div>
      </div>
    );
  }

  private renderGroupPicture(): JSX.Element {
    return (
      <figure class='group-picture-container'>
        {renderResponsiveImages(
          this.group as ResponsiveImages,
          this.imageSizes,
          this.groupPictureText,
          '',
        )}
        <figcaption>{this.groupPictureText}</figcaption>
      </figure>
    );
  }

  private renderQuote(): JSX.Element {
    return (
      <div class='quote-container'>
        <div class='quote-text-wrapper'>
          <cms-quote text={this.quote} />
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host
        style={{
          backgroundImage: `url(${getAssetSrc(this.contactBackgroundImage)})`,
        }}
      >
        <div
          class='container'
          style={this.logo ? null : { 'padding-top': '2rem' }}
        >
          {this.logo ? this.renderLogo() : ''}
          <div class='text-container'>
            {(this.introText || this.introHeadline) && this.renderIntroText()}
            {(this.contactDataPhone ||
              this.contactDataMobile ||
              this.contactDataMail ||
              this.contactDataFax) &&
              this.renderContactData()}
          </div>
          {this.contacts && this.contacts.length > 0 ? (
            <div class='contacts'>
              {this.contacts.map((contact, index) =>
                this.renderContact(contact, index),
              )}
            </div>
          ) : (
            ''
          )}
          {this.group && this.groupPictureText && this.renderGroupPicture()}
          {this.quote && this.renderQuote()}
          {this.ctaLink === '' ? null : (
            <div class='button-wrapper'>
              <cms-button
                class='contact-cta'
                theme='secondary-inverted'
                size='button-md'
                href={this.ctaLink}
                rel='noopener'
                target='_self'
                label={this.ctaText}
                tagTitle={this.ctaText}
              />
            </div>
          )}
        </div>
      </Host>
    );
  }
}
